import React, { useState, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { fetchEvents } from '../../services/eventsService';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';

import PageHeader from '../../components/PageHeader/PageHeader';
import { ReactComponent as HeaderIcon } from '../../assets/site-assets/experience/header-icon.svg';
import { ReactComponent as SenceIcon }  from '../../assets/site-assets/experience/sence-icon.svg';
import { ReactComponent as ArrowIcon }  from '../../assets/site-assets/experience/arrow.svg';

import BgImage         from '../../assets/site-assets/experience/Background.png';
import Monuments       from '../../assets/site-assets/experience/Monuments.png';
import FortsAndCastles from '../../assets/site-assets/experience/Forts-andCastles.png';
import UniqueMosques   from '../../assets/site-assets/experience/Unique-Mosques.png';
import Libraries       from '../../assets/site-assets/experience/Main-Libraries.png';
import Museums         from '../../assets/site-assets/experience/Museums.png';
import Hotels          from '../../assets/site-assets/experience/hotels.png';
import Adventure1      from '../../assets/site-assets/experience/Adventure-1.png';
import Adventure2      from '../../assets/site-assets/experience/Adventure-2.png';
import Adventure3      from '../../assets/site-assets/experience/Adventur-3.png';
import Adventure4      from '../../assets/site-assets/experience/Adventure-4.png';
import Scene1          from '../../assets/site-assets/experience/scene-1.png';
import Scene2          from '../../assets/site-assets/experience/scene-2.png';
import Scene3          from '../../assets/site-assets/experience/scene-3.png';

import './ExperiencePage.scss';

const heritageCardImgs = [Museums, FortsAndCastles, Monuments];
const heritageCardKeys = ['museums', 'forts', 'monuments'];

const culturalCardImgs = [Libraries, UniqueMosques, Hotels];
const culturalCardKeys = ['libraries', 'mosques', 'hotels'];

const adventureCardImgs = [Adventure1, Adventure2, Adventure3, Adventure4];
const sceneFallbackImgs = [Scene1, Scene2, Scene3];

const ExperiencePage = () => {
  const { t, i18n } = useTranslation();
  const [sceneItems, setSceneItems] = useState(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    fetchEvents(i18n.language, 10)
      .then(items => {
        if (!items.length) return;
        setSceneItems(items.map((item, idx) => ({
          title:    item.title,
          link:     item.link || '#',
          category: item.category || 'Event',
          img:      item.image || sceneFallbackImgs[idx % sceneFallbackImgs.length],
        })));
      })
      .catch(() => {}); // falls back to resolvedSceneItems
  }, [i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolvedSceneItems = sceneItems || Array.from({ length: 5 }, (_, i) => ({
    img:      sceneFallbackImgs[i % sceneFallbackImgs.length],
    category: t(`experience.scene.fallback.${i}.category`),
    title:    t(`experience.scene.fallback.${i}.title`),
    link:     '#',
  }));

  return (
    <div className="experience-page">

      <PageHeader
        bgImage={BgImage}
        icon={HeaderIcon}
        title={t('experience.pageTitle')}
        subtitle={t('experience.subtitle')}
      />

      {/* ── Heritage ── */}
      <section className="exp-heritage">
        <div className="exp-container">
          <h2 className="exp-heritage__title">
            <Trans i18nKey="experience.heritage.title" components={{ span: <span /> }} />
          </h2>
          <p className="exp-body">{t('experience.heritage.body1')}</p>
          <p className="exp-body">{t('experience.heritage.body2')}</p>
          <p className="exp-body">
            <span className="exp-heritage__cta">{t('experience.heritage.ctaHighlight')}</span>{' '}
            {t('experience.heritage.ctaText')}
          </p>
          <div className="exp-heritage__grid">
            {heritageCardImgs.map((img, i) => (
              <div key={i} className="exp-card" style={{ backgroundImage: `url(${img})` }}>
                <div className="exp-card__overlay" />
                <div className="exp-card__bottom">
                  <p className="exp-card__title">{t(`experience.heritage.cards.${heritageCardKeys[i]}`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Cultural ── */}
      <section className="exp-cultural">
        <div className="exp-container">
          <h2 className="exp-cultural__title">
            <Trans i18nKey="experience.cultural.title" components={{ span: <span /> }} />
          </h2>
          <p className="exp-body">{t('experience.cultural.body')}</p>
          <div className="exp-cultural__grid">
            {culturalCardImgs.map((img, i) => (
              <div key={i} className="exp-card" style={{ backgroundImage: `url(${img})` }}>
                <div className="exp-card__overlay" />
                <div className="exp-card__bottom">
                  <p className="exp-card__title">{t(`experience.cultural.cards.${culturalCardKeys[i]}`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Adventure ── */}
      <section className="exp-adventure">
        <div className="exp-container exp-adventure__inner">
          <div className="exp-adventure__header">
            <h2 className="exp-adventure__title">{t('experience.adventure.title')}</h2>
            <div className="exp-adventure__meta">
              <p className="exp-adventure__headline">
                <span className="exp-adventure__sharjah">{t('experience.adventure.headlineHighlight')}</span>{' '}
                {t('experience.adventure.headlineMain')}
              </p>
              <p className="exp-adventure__desc">{t('experience.adventure.desc')}</p>
            </div>
          </div>
          <div className="exp-adventure__grid">
            {adventureCardImgs.map((img, i) => (
              <div key={i} className="exp-card exp-card--adventure" style={{ backgroundImage: `url(${img})` }}>
                <div className="exp-card__overlay" />
                <div className="exp-card__bottom">
                  <p className="exp-card__title">{t(`experience.adventure.activities.${i}`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── The Sharjah Scene ── */}
      <section className="exp-scene">
        <div className="exp-scene__top">
          <div className="exp-container">
            <div className="exp-scene__header">
              <h2 className="exp-scene__title">
                <Trans i18nKey="experience.scene.title" components={{ span: <span /> }} />
              </h2>
              <p className="exp-scene__subtitle">{t('experience.scene.subtitle')}</p>
            </div>
          </div>
        </div>

        <div className="exp-scene__bottom">
          <div className="exp-container">
            <div className="exp-scene__swiper-wrap">
              <button ref={prevRef} className="exp-scene__nav exp-scene__nav--prev" aria-label={t('experience.scene.previous')}>
                <ArrowIcon />
              </button>

              <Swiper
                modules={[Navigation]}
                navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
                onSwiper={(swiper) => {
                  swiper.params.navigation.prevEl = prevRef.current;
                  swiper.params.navigation.nextEl = nextRef.current;
                  swiper.navigation.init();
                  swiper.navigation.update();
                }}
                spaceBetween={20}
                breakpoints={{
                  0:    { slidesPerView: 1.15 },
                  640:  { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
                key={i18n.language}
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                className="exp-scene__swiper"
              >
                {resolvedSceneItems.map((item, i) => (
                  <SwiperSlide key={i}>
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="exp-scene-card"
                      style={{ backgroundImage: `url(${item.img})` }}
                    >
                      <div className="exp-scene-card__overlay" />
                      <div className="exp-scene-card__body">
                        <span className="exp-scene-card__badge">{item.category}</span>
                        <p className="exp-scene-card__title">{item.title}</p>
                      </div>
                    </a>
                  </SwiperSlide>
                ))}
              </Swiper>

              <button ref={nextRef} className="exp-scene__nav exp-scene__nav--next" aria-label={t('experience.scene.next')}>
                <ArrowIcon />
              </button>

              <SenceIcon className="exp-scene__icon" aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ExperiencePage;
